import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { fetchTeacherSubjectsWithStudents } from '../../services/api';
import {
  fetchTeacherNotes,
  createTeacherNote,
  updateTeacherNote,
  deleteTeacherNote,
  NOTE_CATEGORIES,
} from '../../services/teacherNotes';
import './TeacherHub.css';

export default function TeacherNotes() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const teacherId = user?.user_id || user?.id || null;

  // Selected Date state (defaults to date passed via location.state or today)
  const [selectedDate, setSelectedDate] = useState(() => {
    return location.state?.selectedDate || new Date().toISOString().split('T')[0];
  });

  // Active Tab Mode: 'subject' | 'class'
  const [activeTabMode, setActiveTabMode] = useState('subject');
  const [selectedSubject, setSelectedSubject] = useState('All Subjects');
  const [selectedClassTab, setSelectedClassTab] = useState('all');

  // Teacher classes and student rosters from live backend
  const [teacherClasses, setTeacherClasses] = useState<any>([]);
  const [availableSubjects, setAvailableSubjects] = useState(['All Subjects', 'Literacy', 'Mathematics', 'Science', 'Social Studies']);
  const [allStudents, setAllStudents] = useState<any>([]);

  // Notes state
  const [notes, setNotes] = useState<any>([]);
  const [loading, setLoading] = useState<any>(true);

  // Search & Filter
  const [search, setSearch] = useState<any>('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [studentFilter, setStudentFilter] = useState('all');

  // Editor Modal / Inline Form
  const [isCreating, setIsCreating] = useState<any>(false);
  const [editingNoteId, setEditingNoteId] = useState<any>(null);

  // Form Fields
  const [formDate, setFormDate] = useState(selectedDate);
  const [formTitle, setFormTitle] = useState<any>('');
  const [formContent, setFormContent] = useState<any>('');
  const [formSubject, setFormSubject] = useState('Literacy');
  const [formCategory, setFormCategory] = useState('Observation');
  const [formStudentId, setFormStudentId] = useState<any>('');
  const [formClassId, setFormClassId] = useState<any>('');
  const [formHasReminder, setFormHasReminder] = useState<any>(false);
  const [formReminderDate, setFormReminderDate] = useState(selectedDate);
  const [formReminderTime, setFormReminderTime] = useState('09:00');

  // Load teacher classes & rosters from live backend
  useEffect(() => {
    async function loadClasses() {
      try {
        const subData = await fetchTeacherSubjectsWithStudents();
        const subjectsList = Array.isArray(subData) ? subData : subData?.subjects || [];
        const classes = [];
        const subjNames = ['All Subjects'];
        const studentsList = [];

        subjectsList.forEach((sub) => {
          const sName = sub.subject_name || sub.subject || 'General';
          if (!subjNames.includes(sName)) subjNames.push(sName);

          (sub.classes || []).forEach((c) => {
            classes.push({
              class_id: String(c.class_id || c.id),
              class_name: c.class_name || c.name || `${sName} - Class ${c.class_id}`,
              subject_name: sName,
              students: c.students || [],
            });

            (c.students || []).forEach((stu) => {
              const sid = String(stu.student_id || stu.id);
              if (!studentsList.some((s) => s.id === sid)) {
                studentsList.push({
                  id: sid,
                  name: stu.full_name || stu.name || `${stu.last_name || ''}, ${stu.first_name || ''}`.trim() || 'Student',
                  className: c.class_name || c.name,
                });
              }
            });
          });
        });

        if (classes.length > 0) setTeacherClasses(classes);
        if (subjNames.length > 1) setAvailableSubjects(subjNames);
        if (studentsList.length > 0) setAllStudents(studentsList);
      } catch (err: any) {
        console.warn('[TeacherNotes] Could not load teacher classes:', err);
      }
    }

    loadClasses();
  }, []);

  // Fetch notes
  const loadNotes = async () => {
    try {
      setLoading(true);
      const data = await fetchTeacherNotes({
        teacherId,
        teacherClasses,
        subject: selectedSubject === 'All Subjects' ? 'all' : selectedSubject,
      });
      setNotes(data);
    } catch (err: any) {
      console.error('Error fetching teacher notes:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teacherId, selectedSubject, teacherClasses.length]);

  // Quick Date Navigation
  const handleShiftDate = (days) => {
    const cur = new Date(selectedDate + 'T00:00:00');
    cur.setDate(cur.getDate() + days);
    const newStr = cur.toISOString().split('T')[0];
    setSelectedDate(newStr);
    setFormDate(newStr);
  };

  const handleSetToday = () => {
    const today = new Date().toISOString().split('T')[0];
    setSelectedDate(today);
    setFormDate(today);
  };

  // Open Create Form
  const handleOpenCreate = () => {
    setEditingNoteId(null);
    setFormDate(selectedDate);
    setFormTitle('');
    setFormContent('');
    setFormSubject(selectedSubject === 'All Subjects' ? (availableSubjects[1] || 'Literacy') : selectedSubject);
    setFormCategory('Observation');
    setFormStudentId('');
    setFormClassId(teacherClasses[0]?.class_id || '');
    setFormHasReminder(false);
    setFormReminderDate(selectedDate);
    setFormReminderTime('09:00');
    setIsCreating(true);
  };

  // Open Edit Form
  const handleOpenEdit = (note) => {
    setEditingNoteId(note.id);
    setFormDate(note.date || selectedDate);
    setFormTitle(note.title || '');
    setFormContent(note.content || '');
    setFormSubject(note.subject || 'Literacy');
    setFormCategory(note.category || 'Observation');
    setFormStudentId(note.studentId || '');
    setFormClassId(note.classId || '');
    setFormHasReminder(Boolean(note.hasReminder));
    setFormReminderDate(note.reminderDate || note.date || selectedDate);
    setFormReminderTime(note.reminderTime || '09:00');
    setIsCreating(true);
  };

  // Save Note
  const handleSaveNote = async (e) => {
    e.preventDefault();
    if (!formTitle.trim()) {
      alert('Please enter a note title.');
      return;
    }

    const studentObj = allStudents.find((s) => s.id === formStudentId);
    const classObj = teacherClasses.find((c) => c.class_id === formClassId);

    const payload = {
      date: formDate,
      title: formTitle.trim(),
      content: formContent.trim(),
      subject: formSubject,
      category: formCategory,
      studentId: formStudentId,
      studentName: studentObj?.name || (formStudentId ? 'Selected Student' : 'All Students'),
      classId: formClassId,
      className: classObj?.class_name || '',
      hasReminder: formHasReminder,
      reminderDate: formHasReminder ? formReminderDate : null,
      reminderTime: formHasReminder ? formReminderTime : null,
    };

    try {
      if (editingNoteId) {
        await updateTeacherNote(editingNoteId, payload, teacherId);
      } else {
        await createTeacherNote(payload, teacherId);
      }
      setIsCreating(false);
      setEditingNoteId(null);
      await loadNotes();
    } catch (err: any) {
      alert(`Could not save note: ${err.message}`);
    }
  };

  // Delete Note
  const handleDeleteNote = async (id, title) => {
    if (!window.confirm(`Delete note "${title}"? This cannot be undone.`)) return;
    try {
      await deleteTeacherNote(id, teacherId);
      await loadNotes();
    } catch (err: any) {
      alert(`Could not delete note: ${err.message}`);
    }
  };

  // Filtered Notes
  const filteredNotes = useMemo(() => {
    return notes.filter((n) => {
      // Tab filter: by class or by subject
      if (activeTabMode === 'class') {
        if (selectedClassTab !== 'all' && String(n.classId) !== String(selectedClassTab)) return false;
      } else {
        if (selectedSubject !== 'All Subjects' && n.subject !== selectedSubject) return false;
      }

      if (categoryFilter !== 'all' && n.category !== categoryFilter) return false;
      if (studentFilter !== 'all' && String(n.studentId) !== String(studentFilter)) return false;

      if (search.trim()) {
        const q = search.toLowerCase().trim();
        const matchesTitle = (n.title || '').toLowerCase().includes(q);
        const matchesContent = (n.content || '').toLowerCase().includes(q);
        const matchesStudent = (n.studentName || '').toLowerCase().includes(q);
        const matchesClass = (n.className || '').toLowerCase().includes(q);
        if (!matchesTitle && !matchesContent && !matchesStudent && !matchesClass) return false;
      }
      return true;
    });
  }, [notes, activeTabMode, selectedClassTab, selectedSubject, categoryFilter, studentFilter, search]);

  // Notes on currently selected date vs other dates
  const notesOnSelectedDate = useMemo(() => {
    return filteredNotes.filter((n) => n.date === selectedDate);
  }, [filteredNotes, selectedDate]);

  const timelineNotes = useMemo(() => {
    return filteredNotes;
  }, [filteredNotes]);

  return (
    <div className="th-container">
      {/* Top Header */}
      <div className="th-header-row">
        <div>
          <h1 className="th-header-title">Teacher Notes & Observations</h1>
          <p className="th-header-subtitle">
            Document date-based classroom observations, student progress reflections, instructional notes, and follow-ups.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            type="button"
            className="th-btn-primary"
            onClick={handleOpenCreate}
          >
            <span>+</span> Add Note
          </button>
          <button
            type="button"
            className="th-btn-secondary"
            onClick={() => navigate('/teacher/calendar')}
          >
            🗓️ Open in Calendar
          </button>
        </div>
      </div>

      {/* Date Switcher Bar (Section 3 Suggested Layout) */}
      <div
        style={{
          background: '#ffffff',
          border: '1px solid #e2e8f0',
          borderRadius: '12px',
          padding: '14px 20px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '12px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '1.4rem' }}>📅</span>
          <div>
            <span style={{ fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: 700, color: '#64748b' }}>
              Selected Note Date
            </span>
            <div style={{ fontSize: '1.1rem', fontWeight: 800, color: '#0f172a' }}>
              {new Date(selectedDate + 'T00:00:00').toLocaleDateString(undefined, {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            type="button"
            className="th-btn-secondary th-btn-sm"
            onClick={() => handleShiftDate(-1)}
            title="Previous Day"
          >
            ← Previous Day
          </button>
          <button
            type="button"
            className="th-btn-secondary th-btn-sm"
            onClick={handleSetToday}
            title="Jump to Today"
          >
            Today
          </button>
          <button
            type="button"
            className="th-btn-secondary th-btn-sm"
            onClick={() => handleShiftDate(1)}
            title="Next Day"
          >
            Next Day →
          </button>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => {
              if (e.target.value) {
                setSelectedDate(e.target.value);
                setFormDate(e.target.value);
              }
            }}
            style={{
              padding: '6px 10px',
              borderRadius: '6px',
              border: '1px solid #cbd5e1',
              fontFamily: 'inherit',
              fontSize: '0.82rem',
              fontWeight: 600,
            }}
          />
        </div>
      </div>

      {/* Tabs Bar: Subject / Class Toggle & Selection (Requirement: tabs to go to each class or subject) */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
          <div style={{ display: 'inline-flex', background: '#f1f5f9', padding: '3px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
            <button
              type="button"
              className={`th-tab-btn ${activeTabMode === 'subject' ? 'active' : ''}`}
              style={{ padding: '6px 14px', fontSize: '0.82rem', margin: 0, borderRadius: '6px' }}
              onClick={() => setActiveTabMode('subject')}
            >
              📚 By Subject
            </button>
            <button
              type="button"
              className={`th-tab-btn ${activeTabMode === 'class' ? 'active' : ''}`}
              style={{ padding: '6px 14px', fontSize: '0.82rem', margin: 0, borderRadius: '6px' }}
              onClick={() => setActiveTabMode('class')}
            >
              🏫 By Class ({teacherClasses.length})
            </button>
          </div>

          <span style={{ fontSize: '0.82rem', color: '#64748b' }}>
            Showing notes for: <strong>{activeTabMode === 'subject' ? selectedSubject : (teacherClasses.find(c => String(c.class_id) === String(selectedClassTab))?.class_name || 'All Classes')}</strong>
          </span>
        </div>

        <div className="th-tabs-bar" style={{ margin: 0 }}>
          {activeTabMode === 'subject' ? (
            availableSubjects.map((subj) => (
              <button
                key={subj}
                type="button"
                className={`th-tab-btn ${selectedSubject === subj ? 'active' : ''}`}
                onClick={() => setSelectedSubject(subj)}
              >
                {subj}
              </button>
            ))
          ) : (
            <>
              <button
                type="button"
                className={`th-tab-btn ${selectedClassTab === 'all' ? 'active' : ''}`}
                onClick={() => setSelectedClassTab('all')}
              >
                All Classes
              </button>
              {teacherClasses.map((cls) => (
                <button
                  key={cls.class_id}
                  type="button"
                  className={`th-tab-btn ${String(selectedClassTab) === String(cls.class_id) ? 'active' : ''}`}
                  onClick={() => setSelectedClassTab(cls.class_id)}
                >
                  {cls.class_name}
                </button>
              ))}
            </>
          )}
        </div>
      </div>

      {/* Main Workspace Layout */}
      <div className="th-notes-layout">
        {/* Left Sidebar: Filters & Date Summary */}
        <div className="th-notes-sidebar">
          <div>
            <h3 style={{ margin: '0 0 12px', fontSize: '0.95rem', color: '#1e293b' }}>
              Filter Notes
            </h3>

            {/* Search */}
            <div style={{ marginBottom: '14px' }}>
              <input
                type="text"
                className="lms-search-input"
                placeholder="Search notes, student, class..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ width: '100%' }}
              />
            </div>

            {/* Category Filter */}
            <div style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#475569', marginBottom: '4px' }}>
                Note Category
              </label>
              <select
                className="lms-select"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                style={{ width: '100%' }}
              >
                <option value="all">All Categories</option>
                {NOTE_CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>

            {/* Student Association Filter */}
            <div style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#475569', marginBottom: '4px' }}>
                Associated Student
              </label>
              <select
                className="lms-select"
                value={studentFilter}
                onChange={(e) => setStudentFilter(e.target.value)}
                style={{ width: '100%' }}
              >
                <option value="all">All Students / General</option>
                {allStudents.map((s) => (
                  <option key={s.id} value={s.id}>{s.name} ({s.className})</option>
                ))}
              </select>
            </div>
          </div>

          <hr style={{ border: 'none', borderTop: '1px solid #e2e8f0', margin: '0' }} />

          {/* Date Summary Card */}
          <div>
            <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>
              Notes on {selectedDate}:
            </div>
            <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#2563eb' }}>
              {notesOnSelectedDate.length} Saved
            </div>
            <p style={{ fontSize: '0.78rem', color: '#64748b', margin: '4px 0 12px' }}>
              Total across timeline: {filteredNotes.length} notes
            </p>
            <button
              type="button"
              className="th-btn-primary th-btn-sm"
              style={{ width: '100%', justifyContent: 'center' }}
              onClick={handleOpenCreate}
            >
              + Add Note for {selectedDate}
            </button>
          </div>
        </div>

        {/* Right Main Content Area */}
        <div className="th-notes-main">
          {/* Note Editor Form (if creating or editing) */}
          {isCreating && (
            <div className="th-note-editor-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h2 style={{ fontSize: '1.15rem', margin: 0, color: '#1e293b' }}>
                  {editingNoteId ? 'Edit Teacher Note' : 'Create Date-Based Note'}
                </h2>
                <button
                  type="button"
                  onClick={() => setIsCreating(false)}
                  style={{ background: 'transparent', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#94a3b8' }}
                >
                  ✕
                </button>
              </div>

              <form onSubmit={handleSaveNote}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginBottom: '14px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: '#334155', marginBottom: '4px' }}>
                      Date *
                    </label>
                    <input
                      type="date"
                      value={formDate}
                      onChange={(e) => setFormDate(e.target.value)}
                      required
                      style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: '#334155', marginBottom: '4px' }}>
                      Subject *
                    </label>
                    <select
                      value={formSubject}
                      onChange={(e) => setFormSubject(e.target.value)}
                      style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                    >
                      {availableSubjects.filter((s) => s !== 'All Subjects').map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: '#334155', marginBottom: '4px' }}>
                      Category *
                    </label>
                    <select
                      value={formCategory}
                      onChange={(e) => setFormCategory(e.target.value)}
                      style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                    >
                      {NOTE_CATEGORIES.map((c) => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: '#334155', marginBottom: '4px' }}>
                      Class
                    </label>
                    <select
                      value={formClassId}
                      onChange={(e) => setFormClassId(e.target.value)}
                      style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                    >
                      <option value="">No specific class (General)</option>
                      {teacherClasses.map((c) => (
                        <option key={c.class_id} value={c.class_id}>{c.class_name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: '#334155', marginBottom: '4px' }}>
                      Student (Optional)
                    </label>
                    <select
                      value={formStudentId}
                      onChange={(e) => setFormStudentId(e.target.value)}
                      style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                    >
                      <option value="">No specific student (General)</option>
                      {allStudents.map((s) => (
                        <option key={s.id} value={s.id}>{s.name} ({s.className})</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Calendar Reminder Configuration (Requirement: setup reminders connected to calendar) */}
                <div style={{
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  padding: '12px 14px',
                  marginBottom: '14px',
                }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 700, color: '#1e293b' }}>
                    <input
                      type="checkbox"
                      checked={formHasReminder}
                      onChange={(e) => setFormHasReminder(e.target.checked)}
                      style={{ width: '16px', height: '16px', accentColor: '#2563eb' }}
                    />
                    <span>⏰ Set Reminder Connected to Unified Calendar</span>
                  </label>

                  {formHasReminder && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '10px' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>
                          Reminder Date
                        </label>
                        <input
                          type="date"
                          value={formReminderDate}
                          onChange={(e) => setFormReminderDate(e.target.value)}
                          required={formHasReminder}
                          style={{ width: '100%', padding: '6px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.82rem' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>
                          Reminder Time
                        </label>
                        <input
                          type="time"
                          value={formReminderTime}
                          onChange={(e) => setFormReminderTime(e.target.value)}
                          required={formHasReminder}
                          style={{ width: '100%', padding: '6px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.82rem' }}
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div style={{ marginBottom: '14px' }}>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: '#334155', marginBottom: '4px' }}>
                    Note Title *
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Small Group Guided Reading Observation"
                    value={formTitle}
                    onChange={(e) => setFormTitle(e.target.value)}
                    required
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.92rem' }}
                  />
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: '#334155', marginBottom: '4px' }}>
                    Observations, Reflections & Instructional Notes *
                  </label>
                  <textarea
                    rows={5}
                    placeholder="Document student observations, learning insights, questions for next session, or instructional modifications..."
                    value={formContent}
                    onChange={(e) => setFormContent(e.target.value)}
                    required
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontFamily: 'inherit', fontSize: '0.88rem', lineHeight: 1.5 }}
                  />
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                  <button
                    type="button"
                    className="th-btn-secondary"
                    onClick={() => setIsCreating(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="th-btn-primary"
                  >
                    {editingNoteId ? 'Update Note' : 'Save Note'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Chronological List of Notes */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
              Loading teacher notes...
            </div>
          ) : timelineNotes.length === 0 ? (
            <div style={{
              background: '#ffffff',
              border: '1px dashed #cbd5e1',
              borderRadius: '12px',
              padding: '40px 20px',
              textAlign: 'center',
            }}>
              <span style={{ fontSize: '2.5rem' }}>📝</span>
              <h3 style={{ margin: '8px 0', color: '#1e293b' }}>No notes found</h3>
              <p style={{ color: '#64748b', fontSize: '0.9rem', maxWidth: '480px', margin: '0 auto 16px' }}>
                {search || categoryFilter !== 'all' || studentFilter !== 'all'
                  ? 'Try adjusting your search criteria or filter selections.'
                  : `No notes documented yet for ${selectedSubject}. Click "+ Add Note" to record your first classroom observation.`}
              </p>
              <button
                type="button"
                className="th-btn-primary"
                onClick={handleOpenCreate}
              >
                + Add Note
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {timelineNotes.map((note) => {
                const categoryConfig = NOTE_CATEGORIES.find((c) => c.value === note.category) || NOTE_CATEGORIES[0];
                const isSelectedDate = note.date === selectedDate;

                return (
                  <div
                    key={note.id}
                    className="th-note-item"
                    style={{
                      borderLeft: isSelectedDate ? '4px solid #2563eb' : '1px solid #e2e8f0',
                      background: isSelectedDate ? '#f8faff' : '#ffffff',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '8px' }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                          <span
                            style={{
                              backgroundColor: `${categoryConfig.color}15`,
                              color: categoryConfig.color,
                              padding: '2px 8px',
                              borderRadius: '6px',
                              fontSize: '0.72rem',
                              fontWeight: 700,
                            }}
                          >
                            {categoryConfig.label}
                          </span>
                          <span className="lms-badge lms-badge-type">
                            {note.subject}
                          </span>
                          {note.studentName && note.studentName !== 'All Students' && (
                            <span
                              style={{
                                backgroundColor: '#f1f5f9',
                                color: '#334155',
                                padding: '2px 8px',
                                borderRadius: '6px',
                                fontSize: '0.72rem',
                                fontWeight: 600,
                              }}
                            >
                              👤 {note.studentName}
                            </span>
                          )}
                        </div>
                        <h3 style={{ margin: '8px 0 4px', fontSize: '1.05rem', color: '#0f172a' }}>
                          {note.title}
                        </h3>
                        <div style={{ fontSize: '0.78rem', color: '#64748b' }}>
                          📅 {note.date} {note.className ? `• ${note.className}` : ''}
                        </div>
                        {note.hasReminder && (
                          <div style={{ marginTop: '6px' }}>
                            <span
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                background: '#fef3c7',
                                color: '#92400e',
                                padding: '2px 8px',
                                borderRadius: '6px',
                                fontSize: '0.72rem',
                                fontWeight: 700,
                                border: '1px solid #fde68a',
                              }}
                            >
                              ⏰ Reminder: {note.reminderDate || note.date} at {note.reminderTime || '09:00'}
                            </span>
                          </div>
                        )}
                      </div>

                      <div style={{ display: 'flex', gap: '6px' }}>
                        {note.hasReminder && (
                          <button
                            type="button"
                            className="th-btn-secondary th-btn-sm"
                            onClick={() => navigate('/teacher/calendar', { state: { selectedDate: note.reminderDate || note.date } })}
                            title="Jump to date on Unified Calendar"
                          >
                            🗓️
                          </button>
                        )}
                        <button
                          type="button"
                          className="th-btn-secondary th-btn-sm"
                          onClick={() => handleOpenEdit(note)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="th-btn-secondary th-btn-sm"
                          style={{ color: '#dc2626' }}
                          onClick={() => handleDeleteNote(note.id, note.title)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>

                    <p style={{ margin: 0, fontSize: '0.88rem', color: '#334155', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                      {note.content}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
